import { Link } from 'react-router-dom';
import {
  FaBookMedical,
  FaCheckCircle,
  FaFileMedicalAlt,
  FaHandshake,
  FaInfoCircle,
  FaShieldAlt,
} from 'react-icons/fa';
import SectionHeading from '../components/SectionHeading';

const resourceCards = [
  {
    title: 'How eligibility is usually determined',
    text: 'Programs generally review functional care needs, provider assessments, and available benefit pathways before service starts.',
    icon: FaFileMedicalAlt,
  },
  {
    title: 'What to prepare before intake',
    text: 'Prepare medication lists, recent medical notes, emergency contacts, and daily routine preferences to speed onboarding.',
    icon: FaBookMedical,
  },
  {
    title: 'Family role in successful home care',
    text: 'Families help by sharing routines, safety concerns, and goals so care plans remain practical and participant-centered.',
    icon: FaHandshake,
  },
];

const externalLinks = [
  {
    title: 'Medicare home health coverage overview',
    href: 'https://www.medicare.gov/coverage/home-health-services',
  },
  {
    title: 'Find healthcare and caregiver support services',
    href: 'https://www.usa.gov/disability-caregiver-support',
  },
  {
    title: 'Compare Medicare-certified home health providers',
    href: 'https://www.medicare.gov/care-compare/?providerType=HomeHealth',
  },
];

export default function ResourcesPage() {
  return (
    <>
      <section className="bg-white py-16">
        <div className="mero-wrapper">
          <SectionHeading
            eyebrow="Family Resources"
            title="Guides and planning tools for home care decisions"
            description="Use these resources to prepare for intake conversations, understand common eligibility steps, and ask better questions before beginning care."
          />

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {resourceCards.map((card) => {
              const Icon = card.icon;

              return (
                <article
                  key={card.title}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
                    <Icon />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">{card.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{card.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mero-wrapper grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <article className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <h2 className="inline-flex items-center gap-2 text-2xl font-bold text-slate-900">
              <FaShieldAlt className="text-teal-700" /> Coverage and service notes
            </h2>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-700">
              <li className="flex items-start gap-3">
                <FaCheckCircle className="mt-1 text-teal-700" />
                Home-based care options can vary based on benefit program, provider documentation, and participant needs.
              </li>
              <li className="flex items-start gap-3">
                <FaCheckCircle className="mt-1 text-teal-700" />
                Some services may require physician orders or periodic reassessment.
              </li>
              <li className="flex items-start gap-3">
                <FaCheckCircle className="mt-1 text-teal-700" />
                Families should request a plain-language breakdown of covered versus non-covered services.
              </li>
            </ul>
          </article>

          <aside className="rounded-2xl border border-slate-200 bg-slate-900 p-7 text-slate-100">
            <h3 className="inline-flex items-center gap-2 text-lg font-semibold text-white">
              <FaInfoCircle className="text-cyan-300" /> Helpful external links
            </h3>
            <ul className="mt-5 space-y-3 text-sm">
              {externalLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-cyan-200 underline-offset-4 hover:underline"
                  >
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="bg-white pb-16">
        <div className="mero-wrapper rounded-3xl border border-slate-200 bg-slate-50 p-8">
          <h2 className="text-2xl font-bold text-slate-900">Need personalized guidance?</h2>
          <p className="mt-3 max-w-3xl text-slate-600">
            Every participant situation is different. Contact our team for a
            personalized walkthrough of care options, paperwork, and next steps.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex rounded-full bg-teal-700 px-6 py-3 text-sm font-semibold text-white transition hover:bg-teal-800"
          >
            Speak With Our Team
          </Link>
        </div>
      </section>
    </>
  );
}
